/**
 * ItemCard Component
 * 
 * Reusable card component for displaying items in grid view.
 * Uses composition pattern for flexible content.
 */

import React from 'react';

export interface ItemCardDetail {
  label: string;
  value: React.ReactNode;
}

export interface ItemCardProps {
  /** Icon element (typically wrapped in styled div) */
  icon: React.ReactNode;
  /** Card title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Status badge element */
  status?: React.ReactNode;
  /** Array of label/value pairs for details section */
  details?: ItemCardDetail[];
  /** Tags/badges section (rendered after details) */
  tags?: React.ReactNode;
  /** Action buttons section (rendered in footer) */
  actions?: React.ReactNode;
  /** Click handler for the entire card (optional) */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function ItemCard({
  icon,
  title,
  subtitle,
  status,
  details,
  tags,
  actions,
  onClick,
  className = '',
}: ItemCardProps) {
  const cardContent = (
    <>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <h3 className="font-medium text-gray-900">{title}</h3>
            {subtitle && (
              <p className="text-sm text-gray-500">{subtitle}</p>
            )}
          </div>
        </div>
        {status}
      </div>
      
      {/* Details */}
      {details && details.length > 0 && (
        <div className="space-y-2 text-sm text-gray-600">
          {details.map((detail, index) => (
            <div key={index} className="flex justify-between">
              <span>{detail.label}:</span>
              <span>{detail.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tags */}
      {tags && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {tags}
        </div>
      )}
      
      {/* Actions */}
      {actions && (
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          {actions}
        </div>
      )}
    </>
  );

  const baseClasses = `bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow ${className}`;

  if (onClick) {
    return (
      <div 
        className={`${baseClasses} cursor-pointer`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div className={baseClasses}>
      {cardContent}
    </div>
  );
}

/**
 * ItemCardIcon - Helper component for consistent icon styling
 */
export interface ItemCardIconProps {
  icon: React.ReactNode;
  bgColor?: string;
}

export function ItemCardIcon({ icon, bgColor = 'bg-blue-100' }: ItemCardIconProps) {
  return (
    <div className={`p-2 ${bgColor} rounded-lg`}>
      {icon}
    </div>
  );
}
